import { useEffect, useState } from "react";
import { CheckCircle2, CloudDownload, XCircle } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDateTime } from "@/lib/constants";
import { Card, LoadingBlock, Spinner } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

const LoginForm = ({ r, onSaved }) => {
  const [f, setF] = useState({ email: r.email, password: "", auto_enabled: r.auto_enabled });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.put("/reelo", f);
      toast.success("Reelo login works and is saved");
      setF((x) => ({ ...x, password: "" }));
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Card title="Reelo login" subtitle="The app logs into Reelo with this account every day and downloads all stores' feedback." testid="reelo-login-card">
      <div className="grid max-w-xl gap-4">
        <div className="space-y-1.5"><Label>Reelo email</Label><Input className="h-11 rounded-xl" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="reelo-email-input" /></div>
        <div className="space-y-1.5"><Label>Password</Label><Input type="password" className="h-11 rounded-xl" placeholder={r.has_password ? "Saved. Type a new one to change it" : ""} value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="reelo-password-input" /></div>
        <label className="flex items-center gap-2 text-sm text-slate-700"><Switch checked={f.auto_enabled} onCheckedChange={(v) => setF({ ...f, auto_enabled: v })} data-testid="reelo-auto-toggle" />Download automatically every day at 11:00 AM (last 3 days, duplicates are skipped)</label>
        <Button className="h-11 w-fit rounded-xl" onClick={save} disabled={busy} data-testid="reelo-save-button">{busy && <Spinner className="mr-2 h-4 w-4" />}Test & save login</Button>
      </div>
    </Card>
  );
};

const RunBox = ({ r, onStarted }) => {
  const [d, setD] = useState({ date_from: "", date_to: "" });
  const go = async () => {
    try {
      const { data } = await api.post("/reelo/run", { date_from: d.date_from || null, date_to: d.date_to || null });
      toast.success(data.message);
      onStarted();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Card title="Download now" subtitle="Leave the dates empty for the last 3 days, or pick a range to fill in older feedback." testid="reelo-run-card">
      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1.5"><Label>From</Label><Input type="date" className="h-10 w-[170px] rounded-xl" value={d.date_from} onChange={(e) => setD({ ...d, date_from: e.target.value })} data-testid="reelo-from-input" /></div>
        <div className="space-y-1.5"><Label>To</Label><Input type="date" className="h-10 w-[170px] rounded-xl" value={d.date_to} onChange={(e) => setD({ ...d, date_to: e.target.value })} data-testid="reelo-to-input" /></div>
        <Button className="h-10 rounded-xl" onClick={go} disabled={!r.has_password || r.running} data-testid="reelo-run-button">
          {r.running ? <Spinner className="mr-2 h-4 w-4" /> : <CloudDownload className="mr-2 h-4 w-4" />}{r.running ? "Downloading…" : "Download from Reelo"}
        </Button>
      </div>
    </Card>
  );
};

const Runs = ({ runs }) => (
  <Card title="Recent downloads" testid="reelo-runs-card">
    {!runs.length ? <p className="text-sm text-slate-400">No downloads yet.</p> : (
      <ul className="divide-y divide-slate-100 text-sm" data-testid="reelo-runs-list">
        {runs.map((x) => (
          <li key={x.id} className="flex items-start gap-3 py-3">
            {x.status === "ok" ? <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" /> : <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-500" />}
            <div><div className="text-slate-800">{x.message}</div><div className="text-xs text-slate-400">{fmtDateTime(x.at)} · {x.trigger === "auto" ? "Automatic" : x.by} · feedback {x.from} to {x.to}</div></div>
          </li>
        ))}
      </ul>
    )}
  </Card>
);

export default function ReeloTab() {
  const { can } = useAuth();
  const [r, setR] = useState(null);
  const load = () => api.get("/reelo").then((x) => setR(x.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => {
    load();
  }, []);
  useEffect(() => {
    if (!r?.running) return undefined;
    const id = setInterval(load, 4000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [r?.running]);
  if (!r) return <LoadingBlock />;
  return (
    <div className="space-y-6" data-testid="reelo-tab">
      {can("admin") && <LoginForm r={r} onSaved={load} />}
      <RunBox r={r} onStarted={load} />
      <Runs runs={r.runs} />
    </div>
  );
}
